import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { AuthService } from '../../../auth';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { PatientSessionService } from '../../../staff/home/patient-session.service';
import { StaffApiService } from '../../../core/api/services/staff-api.service';
import { OfflineDataService } from '../../../core/services/offline-data.service';
import { AlertService } from '../../../shared/services/alert.service';
import { Permissions } from '../../../core/api/models';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { SendLogsService } from '../../../core/services/send-logs.service';
import { AppVersionService } from '../../../core/services/app-version.service';
import { AcademyModeService, SparkModeKey, SparkModeQueryParams } from '../../../core/services/academy-mode.service';
import { SparkLightLoadService } from '../../../core/services/spark-light-load.service';
import { Utils } from '../../../core/utils';
import { SyncSchedulerService } from '../../../core/services/sync-scheduler.service';

@Component({
  selector: 'top-bar',
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.css'],
  standalone: false,
})
export class TopBarComponent implements OnInit, AfterViewInit {
  @Input() showSessionSyncButton: boolean = false;
  @Input() sessionSyncRunning: boolean = false;
  @Output() sessionSyncToggle = new EventEmitter<void>();

  isSignedIn: boolean = false;
  isDownloading: boolean = false;
  isSyncing: boolean = false;
  hasUpdateAvailable: boolean = false;
  deferredPrompt: any = null;
  isInstallable: boolean = false;
  lastDownloadDate: Date | null = null;
  areThereUnsyncedData: boolean = false;
  isRetryingLocalSave = false;

  selectedPatient: { ID: number | undefined; FullName: string } | null = null;
  openMenu: boolean = false;
  patientID: number | null = null;
  loading: boolean = true;
  currentScreen: string = 'staff';
  patientOptions: {
    ID: number | undefined;
    FullName: string;
    searchString: string;
  }[] = [];
  loggedUserID: number | null = null;
  loggedInUsername: string = '';
  isAcademyMode: boolean = false;
  isIsdIopMode: boolean = false;
  isParentMode: boolean = false;

  constructor(
    private authService: AuthService,
    protected patientSessionService: PatientSessionService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private staffApiService: StaffApiService,
    protected offlineDataService: OfflineDataService,
    private alertService: AlertService,
    private sendLogsService: SendLogsService,
    public appVersionService: AppVersionService,
    private academyModeService: AcademyModeService,
    protected lightLoad: SparkLightLoadService,
    private syncSchedulerService: SyncSchedulerService
  ) {}

  ngOnInit() {
    this.syncSparkModeFromUrl();
    this.currentScreen = this.router.url.split('/')[1];
    if (this.currentScreen === 'staff') {
      if (this.router.url.split('/')[2] === 'home') {
        this.currentScreen = 'staff-home';
      } else if (this.router.url.split('/')[2] === 'preview') {
        this.currentScreen = 'staff-preview';
      } else if (this.router.url.split('/')[3] === 'sessions') {
        this.currentScreen = 'staff-sessions';
      }
    }

    this.authService.onStateChanged().forEach((state: boolean) => {
      this.isSignedIn = state;
    });
    this.appVersionService.updateAvailable$().subscribe((hasUpdate) => {
      this.hasUpdateAvailable = hasUpdate;
    });

    this.authService.getUserInfo().subscribe((user) => {
      this.loggedUserID = user?.ID ?? null;
      this.loggedInUsername = user?.UserName ?? user?.Fullnane ?? '';
    });

    this.offlineDataService.getOfflinePatients().subscribe({
      next: (patients) => {
        if (patients && patients.size > 0) {
          const offlinePatients = Array.from(patients.values()).map(
            (data) => data
          );
          const unsyncedPatients = offlinePatients.filter((patient) =>
            patient.sessions.some(
              (session) =>
                (session.isDirty && (session.ID || 0) >= 0) ||
                (patient.progressNotes &&
                  Object.values(patient.progressNotes).some(
                    (note) => (note as any).isDirty
                  ))
            )
          );
          this.areThereUnsyncedData = unsyncedPatients.length > 0;
        } else {
          this.areThereUnsyncedData = false;
        }
      },
      error: (error) => {
        this.areThereUnsyncedData = false;
      },
    });

    this.offlineDataService.checkIsOnline().subscribe((isOnline) => {
      if (isOnline) {
        // Online mode - load patients from API
        if (!this.patientOptions || this.patientOptions.length === 0) {
          this.authService.getMyPatients().subscribe((patients) => {
            if (patients) {
              this.patientOptions = patients.map((patient) => ({
                ID: patient.ID,
                FullName: `${patient.FirstName} ${patient.LastName}`,
                searchString: `${patient.FirstName} ${patient.LastName} ${
                  patient.PreferredName ?? ''
                } ${patient.FirstName?.toLowerCase().substr(
                  0,
                  3
                )}${patient.LastName?.toLowerCase().substr(
                  0,
                  3
                )}`.toLowerCase(),
              }));
              this.setSelectedPatientIfNeeded();
              this.loading = false;
            }
          });
        }
      } else {
        this.loadOfflinePatientData();
      }
    });

    const subscription = this.activatedRoute.params.subscribe((params) => {
      this.patientID = params['patientID'];
      if (isNaN(Number(this.patientID))) {
        this.patientID = this.patientSessionService.getPatientID();
      }

      if (!isNaN(Number(this.patientID))) {
        this.loading = true;

        // Check if offline mode is active
        if (this.offlineDataService.isInOfflineMode()) {
          // Get patient info from offline storage
          const offlineData = this.offlineDataService.getOfflinePatientData(
            Number(this.patientID)
          );
          if (offlineData && offlineData.patient) {
            const patient = offlineData.patient;
            this.selectedPatient = {
              ID: patient.ID,
              FullName: `${patient.FirstName} ${patient.LastName}`,
            };
            this.loading = false;
          } else {
            this.loading = false;
          }
        } else {
          // Online mode - get from API as usual
          this.staffApiService
            .GetPatientInfo({ id: Number(this.patientID) })
            .subscribe({
              next: (patient) => {
                if (patient) {
                  (this.selectedPatient as any) = {
                    ID: patient.ID,
                    FullName: `${patient.FirstName} ${patient.LastName}`,
                  };
                }
                this.loading = false;
              },
              error: (error) => {
                this.loading = false;
                // Check for the specific patient not found error message
                const errorMessage =
                  typeof error === 'string'
                    ? error
                    : error?.message || 'Error loading patient data';
                if (
                  errorMessage.includes(
                    'No patient was found for the requested patientID, you might not have permissions to access this patient.'
                  )
                ) {
                  // The error interceptor should have already shown the alert, but ensure it's displayed
                  this.alertService.error(errorMessage);
                }
                // Don't show generic error for other cases to avoid duplicate alerts
              },
            });
        }

        if (
          this.patientID?.toString() !==
          this.patientSessionService.getPatientID()?.toString()
        ) {
          this.patientSessionService.initializePatientSession(
            Number(this.patientID),
            this.currentScreen === 'staff-preview' ? -1 : undefined
          );
        }
      }
    });

    if (this.patientID) {
      this.checkLastDownloadDate();
    }

    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.isInstallable = true;
    });

    window.addEventListener('appinstalled', () => {
      this.isInstallable = false;
      this.deferredPrompt = null;
    });

    }

  // Load patient data from offline storage
  private loadOfflinePatientData(): void {
    // Get all offline patients
    this.offlineDataService.getOfflinePatients().subscribe((patients) => {
      if (patients && patients.size > 0) {
        this.patientOptions = Array.from(patients.values())
          .map((data) => data.patient)
          .map((patient) => ({
            ID: patient.ID,
            FullName: `${patient.FirstName} ${patient.LastName}`,
            searchString: `${patient.FirstName} ${patient.LastName} ${
              patient.PreferredName ?? ''
            } ${patient.FirstName?.toLowerCase().substr(
              0,
              3
            )}${patient.LastName?.toLowerCase().substr(0, 3)}`.toLowerCase(),
          }));
        this.setSelectedPatientIfNeeded();
        this.loading = false;
      }
    });
  }

  private setSelectedPatientIfNeeded(): void {
    if (!this.selectedPatient && this.currentScreen === 'staff-home') {
      const urlPatientID = this.getPatientIDFromUrl(this.router.url);
      if (
        urlPatientID &&
        this.patientOptions.find(
          (patient) => patient.ID === Number(urlPatientID)
        )
      ) {
        this.selectedPatient =
          this.patientOptions.find(
            (patient) => patient.ID === Number(urlPatientID)
          ) ?? null;
      }
    }
  }

  private getPatientIDFromUrl(url: string): number | null {
    const urlParts = url.split('/');
    return urlParts[3] ? Number(urlParts[3]) : null;
  }

  openOfflinePatients(): void { this.router.navigate(['/offline']); }

  signOut() {
    this.authService.signOut().subscribe(() => {
      this.router.navigate(['/auth/signin']);
    });
  }

  turnOffLightLoad(): void {
    this.openMenu = false;
    const native = (window as any).__sparkNative;
    if (native && typeof native.setLightLoad === 'function') {
      native.setLightLoad(false);
    } else {
      this.lightLoad.setEnabled(false);
    }
    window.location.reload();
  }

  installApp(): void {
    if (!this.deferredPrompt) {
      this.alertService.confirm(
        'Install Instructions',
        'To install this app: \n\n' +
          'For Chrome/Edge: Click the install icon (⊕) in the address bar\n' +
          'For Safari: Tap Share > Add to Home Screen\n\n' +
          "If you don't see these options, the app may already be installed or your browser may not support PWA installation."
      );
      return;
    }

    this.deferredPrompt.prompt();
    this.deferredPrompt.userChoice.then((choiceResult: { outcome: string }) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }

      this.deferredPrompt = null;
      this.isInstallable = false;
    });
  }

  updateApp(): void {
    if (this.hasUpdateAvailable) this.appVersionService.requestUpdatePrompt();
  }

  ngAfterViewInit(): void {}

  clickOutside() {
    this.openMenu = false;
  }

  switchToStaff() {
    const patientSession = this.patientSessionService.getPatientSession();
    this.router.navigate(
      ['/staff/home/' + this.selectedPatient?.ID + '/' + patientSession?.ID],
      { queryParams: this.getAcademyModeQueryParams() }
    );
  }
  switchToPreviewSession() {
    this.router.navigate(['/staff/preview/' + this.selectedPatient?.ID], {
      queryParams: this.getAcademyModeQueryParams(),
    });
  }

  switchToSessionList() {
    this.router.navigate(['/staff/' + this.selectedPatient?.ID + '/sessions'], {
      queryParams: this.getAcademyModeQueryParams(),
    });
  }

  switchToGraphs() {
    this.router.navigate(['/graphs', this.selectedPatient?.ID], {
      queryParams: this.getAcademyModeQueryParams(),
    });
  }

  switchToSupervisor() {
    this.router.navigate(
      ['/supervisor/' + this.selectedPatient?.ID + '/overview'],
      { queryParams: this.getAcademyModeQueryParams() }
    );
  }

  backToOpto() {
    window.location.href = this.authService.getOptoURL() ?? '/';
  }

  changedPatient(event: { originalEvent?: Event; value: number }) {
    const newPatientIDSelected = event.value;
    // Check if we're in offline mode
    if (this.offlineDataService.isInOfflineMode()) {
      // Get patient info from offline storage
      const offlineData =
        this.offlineDataService.getOfflinePatientData(newPatientIDSelected);
      if (offlineData && offlineData.patient) {
        const patient = offlineData.patient;
        this.selectedPatient = {
          ID: patient.ID,
          FullName: `${patient.FirstName} ${patient.LastName}`,
        };
      } else {
        this.selectedPatient =
          this.patientOptions?.find(
            (patient) => patient.ID === newPatientIDSelected
          ) ?? null;
      }
    } else {
      // Online mode - use the patientOptions list as before
      this.selectedPatient =
        this.patientOptions?.find(
          (patient) => patient.ID === newPatientIDSelected
        ) ?? null;
    }

    this.checkLastDownloadDate();

    if (this.currentScreen === 'staff-home') {
      this.router.navigate(['/staff/' + newPatientIDSelected + '/sessions'], {
        queryParams: this.getAcademyModeQueryParams(),
      });
    } else if (this.currentScreen === 'supervisor') {
      this.router.navigate(['/supervisor/' + newPatientIDSelected + '/overview'], {
        queryParams: this.getAcademyModeQueryParams(),
      });
    } else if (this.currentScreen === 'graphs') {
      this.router.navigate(['/graphs/' + newPatientIDSelected], {
        queryParams: this.getAcademyModeQueryParams(),
      });
    } else if (this.currentScreen === 'settings') {
      this.router.navigate(['/supervisor/' + newPatientIDSelected + '/overview'], {
        queryParams: this.getAcademyModeQueryParams(),
      });
    } else if (this.currentScreen === 'staff-sessions') {
      this.router.navigate(['/staff/' + newPatientIDSelected + '/sessions'], {
        queryParams: this.getAcademyModeQueryParams(),
      });
    } else if (this.currentScreen === 'staff-preview') {
      this.router.navigate(['/staff/preview/' + newPatientIDSelected], {
        queryParams: this.getAcademyModeQueryParams(),
      });
    }
  }

  toggleAcademyMode(): void {
    this.toggleSparkMode(this.isAcademyMode ? 'standard' : 'academy');
  }

  toggleIsdIopMode(): void {
    this.toggleSparkMode(this.isIsdIopMode ? 'standard' : 'isd_iop');
  }

  toggleParentMode(): void {
    this.toggleSparkMode(this.isParentMode ? 'standard' : 'parent');
  }

  private toggleSparkMode(targetMode: SparkModeKey): void {
    this.alertService
      .confirm(
        'Switch Spark Mode',
        `This will switch Spark to ${this.academyModeService.getModeDisplayName(
          targetMode
        )} and reload all data on the screen. Continue?`
      )
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.academyModeService.setSparkMode(targetMode);
        this.isAcademyMode = targetMode === 'academy';
        this.isIsdIopMode = targetMode === 'isd_iop';
        this.isParentMode = targetMode === 'parent';

        const queryParams =
          targetMode === 'standard'
            ? this.academyModeService.getClearedSparkModeQueryParams()
            : this.academyModeService.getSparkModeQueryParams(targetMode);

        this.router
          .navigate([], {
            queryParams,
            queryParamsHandling: 'merge',
            replaceUrl: true,
          })
          .then(() => window.location.reload());
      });
  }

  async retryLocalSave(failure: { patientId: number; sessionID: number; modeKey: SparkModeKey }): Promise<void> {
    if (this.isRetryingLocalSave) return;
    this.isRetryingLocalSave = true;
    try {
      if (await this.offlineDataService.retryOfflineSessionSave(failure.patientId, failure.sessionID, failure.modeKey)) {
        this.syncSchedulerService.markDirty('device save retried');
      } else {
        this.alertService.error('The session could not be saved. Keep Spark open and retry in its original Spark mode.');
      }
    } catch {
      this.alertService.error('The session could not be saved. Keep Spark open and retry saving.');
    } finally {
      this.isRetryingLocalSave = false;
    }
  }

  syncPatientData(): void {
    this.isSyncing = true;
    this.offlineDataService.checkIsOnline().subscribe((isOnline) => {
      if (isOnline) {
        this.offlineDataService
          .syncOfflineDataWithGroupReconciliation()
          .subscribe({
          next: () => {
            this.isSyncing = false;
            this.alertService.success('Patient data synced successfully');
          },
          error: (err) => {
            this.isSyncing = false;
            const message = Utils.getSyncErrorMessage(err);
            this.alertService.stickyError('Failed to sync patient data - ' + message);
            this.sendLogsService.addError(
              'Failed to sync patient data: ' + message,
              err?.stack
            );
          },
        });
      } else {
        this.alertService.error('Cannot sync patient data while offline');
        this.isSyncing = false;
      }
    });
  }

  downloadPatientProfile(): void {
    if (!this.selectedPatient?.ID) {
      this.alertService.error('No patient selected');
      return;
    }

    if (this.offlineDataService.isInOfflineMode()) {
      this.alertService.error('Cannot download patient data while offline');
      return;
    }

    this.isDownloading = true;
    this.offlineDataService
      .savePatientForOffline(this.selectedPatient.ID)
      .subscribe(
        (success) => {
          this.isDownloading = false;
          if (success) {
            this.lastDownloadDate = new Date();
            this.alertService.success(
              'Patient profile downloaded successfully'
            );
          } else {
            console.log('Failed to renew patient profile for offline');
          }
        },
        (error) => {
          this.isDownloading = false;
          console.error('Error downloading patient profile:', error);
          this.alertService.error('Error downloading patient profile');
        }
      );
  }

  getDownloadTooltip(): string {
    if (this.isDownloading) {
      return 'Downloading patient profile...';
    }
    if (this.lastDownloadDate) {
      return 'Update offline profile';
    }
    return 'Download patient profile for offline access';
  }

  sendLogs(): void {
    const raw = this.appVersionService.getVersionRaw();
    const versionLine = raw ? `App version: ${raw}\n\n` : '';
    const detail = `${versionLine}This will send diagnostic information to the server (including unsynced data if any). Continue?`;
    this.alertService.confirm('Send logs', detail).subscribe((result) => {
      if (result) {
        const currentSession = this.patientSessionService.getPatientSession();
        this.sendLogsService.sendLogs(
          `Top-bar send logs; route=${this.router.url}; selectedPatientID=${this.selectedPatient?.ID ?? 'none'}; patientID=${this.patientID ?? 'none'}; sessionID=${currentSession?.ID ?? 'none'}`
        );
      }
    });
  }

  getSyncTooltip(): string {
    if (this.isSyncing) {
      return 'Syncing patient data...';
    }
    return 'Sync patient data';
  }

  private checkLastDownloadDate(): void {
    if (this.patientID) {
      const offlineData = this.offlineDataService.getOfflinePatientData(
        Number(this.patientID)
      );
      if (offlineData) {
        this.lastDownloadDate = offlineData.lastSyncTime;
      }
    }
  }

  // Add these methods to check user permissions

  /**
   * Check if the user has supervisor permissions
   */
  hasSupervisorPermission(): boolean {
    const userInfo = this.authService.userInfo;
    if (!userInfo || !userInfo.Permissions) {
      return false;
    }

    return userInfo.Permissions.includes(Permissions.SparkApp_Supervisor);
  }

  /**
   * Check if the user has staff permissions
   */
  hasStaffPermission(): boolean {
    const userInfo = this.authService.userInfo;
    if (!userInfo || !userInfo.Permissions) {
      return false;
    }

    return userInfo.Permissions.includes(Permissions.SparkApp_Staff);
  }

  /**
   * Check if the user has settings access permission
   */
  hasSettingsPermission(): boolean {
    const userInfo = this.authService.userInfo;
    if (!userInfo || !userInfo.Permissions) {
      return false;
    }

    return userInfo.Permissions.includes(Permissions.SparkApp_GeneralSettings);
  }

  /**
   * Check if the user has graphs access permission
   */
  hasGraphsPermission(): boolean {
    const userInfo = this.authService.userInfo;
    if (!userInfo || !userInfo.Permissions) {
      return false;
    }

    // If user has supervisor permissions, they can access graphs
    if (this.hasSupervisorPermission()) {
      return true;
    }

    // No specific Graphs permission found, so allow staff to view graphs
    return this.hasStaffPermission();
  }

  hasAcademyModePermission(): boolean {
    const userInfo = this.authService.userInfo;
    if (!userInfo || !userInfo.Permissions) {
      return false;
    }

    return userInfo.Permissions.includes(Permissions.SparkApp_AcademyMode);
  }

  hasIsdIopModePermission(): boolean {
    const userInfo = this.authService.userInfo;
    if (!userInfo || !userInfo.Permissions) {
      return false;
    }

    return userInfo.Permissions.includes(Permissions.SparkApp_IsdIopMode);
  }

  hasParentModePermission(): boolean {
    const userInfo = this.authService.userInfo;
    if (!userInfo || !userInfo.Permissions) {
      return false;
    }

    return userInfo.Permissions.includes(Permissions.SparkApp_ParentMode);
  }

  private syncSparkModeFromUrl(): void {
    const mode = this.academyModeService.syncModeFromCurrentUrl();
    this.isAcademyMode = mode === 'academy';
    this.isIsdIopMode = mode === 'isd_iop';
    this.isParentMode = mode === 'parent';
  }

  getAcademyModeQueryParams(): SparkModeQueryParams {
    return this.academyModeService.getSparkModeQueryParams();
  }
}
